<script setup lang="ts">
  import { Button } from '@/components/ui/button'
  import { CheckCircle, Loader2, AlertTriangle, X } from 'lucide-vue-next'
  import { useTranslation } from '@/composables/useTranslation'
  import { computed } from 'vue'

  interface Props {
    status: 'syncing' | 'success' | 'error' | null
    count?: number
    errorMessage?: string
  }

  interface Emits {
    (e: 'retry'): void
    (e: 'dismiss'): void
  }

  const props = defineProps<Props>()
  const emit = defineEmits<Emits>()

  const { t } = useTranslation({
    en: {
      syncingMessage: '__COUNT__ document is currently syncing...',
      syncingMessagePlural: '__COUNT__ documents are currently syncing...',
      successMessage: '__COUNT__ document has been successfully synced.',
      successMessagePlural: '__COUNT__ documents have been successfully synced.',
      errorMessage: "We couldn't complete the documents sync.",
      retryButton: 'Retry the sync'
    },
    de: {
      syncingMessage: '__COUNT__ Dokument wird derzeit synchronisiert...',
      syncingMessagePlural: '__COUNT__ Dokumente werden derzeit synchronisiert...',
      successMessage: '__COUNT__ Dokument wurde erfolgreich synchronisiert.',
      successMessagePlural: '__COUNT__ Dokumente wurden erfolgreich synchronisiert.',
      errorMessage: 'Die Dokumentensynchronisierung konnte nicht abgeschlossen werden.',
      retryButton: 'Synchronisierung wiederholen'
    }
  })

  const syncingMessage = computed(() => {
    const count = props.count ?? 0
    const key = count === 1 ? 'syncingMessage' : 'syncingMessagePlural'
    return t(key).replace('__COUNT__', count.toString())
  })

  const successMessage = computed(() => {
    const count = props.count ?? 0
    const key = count === 1 ? 'successMessage' : 'successMessagePlural'
    return t(key).replace('__COUNT__', count.toString())
  })

  const bannerClass = computed(() => {
    if (props.status === 'syncing') return 'border-blue-200 bg-blue-50'
    if (props.status === 'success') return 'border-green-200 bg-green-50'
    if (props.status === 'error') return 'border-red-200 bg-red-50'
    return ''
  })

  const iconClass = computed(() => {
    if (props.status === 'syncing') return 'text-blue-600'
    if (props.status === 'success') return 'text-green-600'
    if (props.status === 'error') return 'text-red-600'
    return ''
  })
</script>

<template>
  <div
    v-if="status"
    class="mb-4 flex min-h-[52px] items-center justify-between rounded-lg border px-4 py-3"
    :class="bannerClass"
  >
    <div class="flex items-center gap-3">
      <Loader2
        v-if="status === 'syncing'"
        class="h-5 w-5 animate-spin"
        :class="iconClass"
      />
      <CheckCircle
        v-else-if="status === 'success'"
        class="h-5 w-5"
        :class="iconClass"
      />
      <AlertTriangle
        v-else-if="status === 'error'"
        class="h-5 w-5"
        :class="iconClass"
      />

      <div>
        <p
          v-if="status === 'syncing'"
          class="text-sm font-medium text-gray-900"
        >
          {{ syncingMessage }}
        </p>
        <p
          v-else-if="status === 'success'"
          class="text-sm font-medium text-gray-900"
        >
          {{ successMessage }}
        </p>
        <div v-else-if="status === 'error'">
          <p class="text-sm font-medium text-red-900">
            {{ t('errorMessage') }}
          </p>
          <p
            v-if="errorMessage"
            class="text-sm text-red-700"
          >
            {{ errorMessage }}
          </p>
        </div>
      </div>
    </div>

    <div class="flex items-center gap-2">
      <Button
        v-if="status === 'error'"
        variant="outline"
        size="sm"
        class="gap-2 border-red-300 text-red-700 hover:bg-red-100"
        @click="emit('retry')"
      >
        <span class="text-lg leading-none">+</span>
        {{ t('retryButton') }}
      </Button>
      <Button
        variant="ghost"
        size="icon"
        class="h-8 w-8"
        @click="emit('dismiss')"
      >
        <X class="h-4 w-4" />
      </Button>
    </div>
  </div>
</template>
